import { flattenDeep } from 'lodash';

import { FiscalItem, ItemUnit, TaxRate } from '@dreryk/egabinet-invoice-frontend-api';
import { VisitMedicalService, VisitMedicalServiceParameters } from '@dreryk/egabinet-financial-settlement-frontend-api';
import { Money } from '@dreryk/money-common-api';

import { assertUnreachable } from '@dreryk/react-common';

import { VisitSettlementControl } from '../../visitSettlement.types';
import { getVisitSettlementValue } from '../../visitSettlement.utils';

import { VisitMedicalServiceTableBean } from './medicalServices.types';

const MEDICAL_SERVICE_QUANTITY = 1;
const MEDICAL_SERVICE_UNIT: ItemUnit = 'SERVICE';

export const triggerAllMedicalServices = async (control: VisitSettlementControl): Promise<boolean> => {
   const visitSettlement = getVisitSettlementValue(control);

   const fieldsToTrigger = flattenDeep(
      visitSettlement?.medicalServices?.map((medicalService, index) =>
         Object.keys(medicalService).map((key) => `visitSettlement.medicalServices[${index}].${key}`)
      )
   );
   return control.trigger(fieldsToTrigger).catch(() => true);
};

export const getMedicalServicePrices = (medicalServices: VisitMedicalServiceTableBean[]): number[] => {
   return (medicalServices || []).map((visitMedicalService) => visitMedicalService.price!);
};

export const mapToVisitMedicalServicesTableBean = ({
   medicalService,
   price,
}: VisitMedicalService): VisitMedicalServiceTableBean => {
   return {
      medicalService: {
         id: medicalService.id,
         name: medicalService.name,
         priceListPrice: medicalService.priceListPrice.asNumber(),
      },
      price: price.asNumber(),
   };
};

export const medicalServicesToVisitMedicalServiceParameters = (
   medicalServices: VisitMedicalServiceTableBean[]
): VisitMedicalServiceParameters[] => {
   return medicalServices.map((visitMedicalService) => {
      return {
         medicalServiceId: visitMedicalService.medicalService.id,
         price: Money.of(visitMedicalService.price!),
      };
   });
};

export enum MedicalServiceTaxRateMap {
   ZW = 'ZW',
   ZERO = 'ZERO',
   REDUCED = 'REDUCED',
   STANDARD = 'STANDARD',
}

export const mapMedicalServiceTaxRateCodeToTaxRate = (taxRateCode: string): TaxRate => {
   switch (taxRateCode) {
      case MedicalServiceTaxRateMap.ZW:
         return 'NO_VAT';
      case MedicalServiceTaxRateMap.ZERO:
         return 'VAT_0';
      case MedicalServiceTaxRateMap.REDUCED:
         return 'VAT_8';
      case MedicalServiceTaxRateMap.STANDARD:
         return 'VAT_23';
      default:
         return assertUnreachable(undefined);
   }
};

export const mapVisitMedicalServiceToFiscalItems = (medicalServices: VisitMedicalService[]): FiscalItem[] => {
   return medicalServices.map(({ medicalService, price }) => ({
      name: medicalService.nameForReceipt,
      quantity: MEDICAL_SERVICE_QUANTITY,
      unit: MEDICAL_SERVICE_UNIT,
      unitPrice: price,
      taxRate: mapMedicalServiceTaxRateCodeToTaxRate(medicalService.taxRateCode),
   }));
};
